/**
 * DevDash Gating Logic
 *
 * Controls when DevDash is available:
 *   1. OFF if env var NEXT_PUBLIC_DEVDASH_DISABLED=true
 *   2. Non-production (develop, qa, local): ON automatically — no opt-in needed
 *   3. Production: ON after visiting ?devdash=true (no password), then Ctrl+. to toggle
 */

const STORAGE_KEY_ENABLED = 'devdash.enabled';
const URL_PARAM_ENABLE = 'devdash';

/**
 * Get the current environment
 */
// biome-ignore lint/nursery/useExportsLast: Function exported at definition for code organization
export function getCurrentEnvironment(): string {
  // biome-ignore lint/style/noProcessEnv: DevDash needs direct environment access for gating
  return process.env.ENVIRONMENT?.toLowerCase() || 'local';
}

// biome-ignore lint/nursery/useExportsLast: Function exported at definition for code organization
export function isProductionEnvironment(): boolean {
  const env = getCurrentEnvironment();
  return env === 'prod' || env === 'production';
}

/**
 * Check if DevDash is disabled via environment variable
 */
function isDisabledByEnvVar(): boolean {
  // biome-ignore lint/style/noProcessEnv: DevDash needs direct environment access for gating
  return process.env.NEXT_PUBLIC_DEVDASH_DISABLED === 'true';
}

/**
 * Check if the user has enabled DevDash via localStorage (opt-in)
 */
export function isDevDashEnabledByUser(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return localStorage.getItem(STORAGE_KEY_ENABLED) === 'true';
  } catch {
    return false;
  }
}

/**
 * Enable DevDash by setting localStorage flag
 */
export function enableDevDash(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(STORAGE_KEY_ENABLED, 'true');
  } catch {
    // Ignore storage errors
  }
}

/**
 * Disable DevDash by removing localStorage flag (logout)
 */
export function disableDevDash(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(STORAGE_KEY_ENABLED);
  } catch {
    // Ignore storage errors
  }
}

/** Result of the URL-based DevDash activation check */
export type ActivationResult = 'activated' | 'deactivated' | 'none';

/**
 * Clean the devdash parameter from the current URL
 */
function cleanDevDashParam(): void {
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.delete(URL_PARAM_ENABLE);
  const newSearch = urlParams.toString();
  const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
  window.history.replaceState({}, '', newUrl);
}

/**
 * Check URL for DevDash enable/disable parameter.
 * No password is required — ?devdash=true enables, ?devdash=false disables.
 *
 * Returns:
 *   'activated'   - DevDash was enabled via URL param
 *   'deactivated' - ?devdash=false was used to disable
 *   'none'        - no devdash param in URL
 */
export function checkUrlForDevDashEnable(): ActivationResult {
  if (typeof window === 'undefined') {
    return 'none';
  }

  try {
    const urlParams = new URLSearchParams(window.location.search);
    const devdashParam = urlParams.get(URL_PARAM_ENABLE);

    if (devdashParam === null) {
      return 'none';
    }

    if (devdashParam === 'false') {
      disableDevDash();
      cleanDevDashParam();
      return 'deactivated';
    }

    if (devdashParam === 'true') {
      cleanDevDashParam();
      enableDevDash();
      return 'activated';
    }

    cleanDevDashParam();
    return 'none';
  } catch {
    return 'none';
  }
}

/**
 * Main gating function - determines if DevDash should be available
 *
 * Gating rules (checked in order):
 *   1. OFF if env var NEXT_PUBLIC_DEVDASH_DISABLED=true
 *   2. Non-production: ON automatically
 *   3. Production: ON if user visited ?devdash=true (localStorage)
 *   4. OFF otherwise
 */
export function isDevDashEnabled(): boolean {
  if (isDisabledByEnvVar()) {
    return false;
  }

  if (!isProductionEnvironment()) {
    return true;
  }

  if (isDevDashEnabledByUser()) {
    return true;
  }

  return false;
}

/**
 * Get environment badge color for display
 */
export function getEnvBadgeColor(): string {
  const env = getCurrentEnvironment();
  switch (env) {
    case 'local':
      return '#10b981'; // Green
    case 'dev':
      return '#3b82f6'; // Blue
    case 'qa':
      return '#8b5cf6'; // Purple
    case 'prod':
    case 'production':
      return '#ef4444'; // Red
    case 'stage':
      return '#f59e0b'; // Amber
    default:
      return '#6b7280'; // Gray
  }
}
